import { debounce, isEqual } from "es-toolkit";
import { makeAutoObservable, reaction, runInAction } from "mobx";

import { pyodideManager } from "@/components/pyodide/PyodideManager";
import {
  getInlineMathHints,
  getPhantomHints,
  inlineMathInputKey,
} from "@/components/suggestions/providers/utils/getPhantomHints";
import {
  getTableRowFormulaCells,
  isRowHeader,
  tableCellHintKey,
} from "@/components/suggestions/providers/utils/getTableCellFormula";
import { getTableCellHint } from "@/components/suggestions/providers/utils/getTableCellHint";
import { cursorIsActive } from "@/models/CursorModel";
import { trackEvent } from "@/utils/trackEvent";

import type { TableRowHintCell } from "@/components/suggestions/providers/utils/getTableCellFormula";
import type { PhantomHint } from "@/components/suggestions/types";
import type { PhantomHintItem } from "@/components/tiptap/phantom-hint/PhantomHint";
import type { DocumentModel } from "@/models/DocumentModel";
import type { EditorModel } from "@/models/EditorModel";
import type { NodeJson } from "@corca/data";

/** Idle delay before recomputing "=" autocomplete phantom hints (D-2714). */
const INLINE_MATH_HINTS_DEBOUNCE_MS = 400;

/** Where a hint sits: a fixed document offset (a table cell) or wherever the cursor is. */
type HintPlacement = number | "head";

/**
 * One place a phantom hint applies, with its candidate `variants` (competing commands) and their
 * resolved `results` (parallel array; `undefined` = not yet resolved, `null` = no real result).
 *
 * A cursor-position expression hint is a single `"head"` position with many variants (the user
 * cycles them with Shift+Tab). Table-row hints are many numeric positions with one variant each.
 * A single variant index is shared across all positions.
 */
type HintPosition = {
  placement: HintPlacement;
  variants: PhantomHint[];
  results: (NodeJson | null | undefined)[];
};

function makeHintPosition(placement: HintPlacement, variants: PhantomHint[]): HintPosition {
  const results = variants.map<NodeJson | null | undefined>(() => undefined);
  return { placement, variants, results };
}

/**
 * Owns all phantom-hint state and lifecycle: the idle "=" autocomplete hint, the alternative
 * commands the user cycles through, and the accept-all table-row hints. Held by EditorModel as
 * `editor.hints`; reads editor state (tiptap, selection, suggestion mode) and writes the hint
 * decoration + dependency highlight back through the editor.
 */
export class PhantomHintsModel {
  private get doc(): DocumentModel {
    return this.editor.doc;
  }

  private hintsDisposer?: () => void;

  hintStatus: "idle" | "computing" = "idle";

  private hintCancelTokens: { cancelled: boolean }[] = [];

  private shownPhantomHintLabels = new Set<string>();

  private _hintPositions: HintPosition[] = [];

  private _variantIndex = 0;

  constructor(private editor: EditorModel) {
    makeAutoObservable<
      typeof this,
      "editor" | "hintCancelTokens" | "runInlineMathHints" | "runTableCellHints"
    >(this, {
      editor: false,
      hintCancelTokens: false,
      runInlineMathHints: false,
      scheduleHints: false,
      runTableCellHints: false,
    });
  }

  /**
   * Install the hint watchers. Called after the owning DocumentModel finishes wiring (the cursor
   * map is not ready during construction, which runs mid-DocumentModel construction).
   */
  initWatchers() {
    this.initHintsWatcher();
  }

  dispose() {
    this.hintsDisposer?.();
    this.scheduleHints.cancel();
  }

  private initHintsWatcher() {
    if (this.hintsDisposer) return;
    this.hintsDisposer = reaction(
      () => {
        const {
          tiptap,
          textSelection: { from, to },
        } = this.editor;
        if (!tiptap) return null;
        void from;
        void to;

        return tableCellHintKey(this.doc, tiptap) ?? inlineMathInputKey(this.doc);
      },
      (key, prevKey) => {
        if (key === null) {
          this.scheduleHints.cancel();
          if (prevKey?.startsWith("table:")) {
            this.setPhantomHints([]);
          }
        } else {
          this.scheduleHints(key);
        }
      },
    );
  }

  private runInlineMathHints = (scheduledKey: string) => {
    if (this.editor.suggestions.mode === "active") return;
    if (!pyodideManager.isReady) return;
    // Snapshot validation: bail if the user moved/edited/dismissed during the debounce window.
    if (inlineMathInputKey(this.doc) !== scheduledKey) return;
    this.setPhantomHints(getInlineMathHints(this.doc));
  };

  private runTableCellHints = (scheduledKey: string) => {
    const { tiptap } = this.editor;
    if (this.editor.suggestions.mode === "active") return;
    if (!pyodideManager.isReady || !tiptap) return;
    // Snapshot validation: bail if the selection moved during the debounce window.
    if (tableCellHintKey(this.doc, tiptap) !== scheduledKey) return;

    // Cursor in a row-header formula cell with an all-empty data row: fill every cell at once.
    const rowCells = getTableRowFormulaCells(this.doc, tiptap);
    if (rowCells) {
      this.setTableRowHints(rowCells);
      return;
    }

    if (!isRowHeader(tiptap)) {
      this.setPhantomHints(getPhantomHints(this.doc));
    }
  };

  scheduleHints = debounce((key: string) => {
    if (key.startsWith("table:")) {
      this.runTableCellHints(key);
    } else {
      this.runInlineMathHints(key);
    }
  }, INLINE_MATH_HINTS_DEBOUNCE_MS);

  /** The lone cursor-following position, when the current hints are a cursor-placed expression. */
  private get cursorHintPosition(): HintPosition | null {
    const [first] = this._hintPositions;
    return this._hintPositions.length === 1 && first?.placement === "head" ? first : null;
  }

  get phantomHintResult(): NodeJson | null {
    return this.cursorHintPosition?.results[this._variantIndex] ?? null;
  }

  /** The active variant (with its command/label) of the cursor-placed expression hint. */
  get phantomHint(): PhantomHint | null {
    return this.cursorHintPosition?.variants[this._variantIndex] ?? null;
  }

  get previewHintShown(): boolean {
    const hint = this.phantomHint;
    return !!hint && hint.type === "reference";
  }

  get previewAtCursor(): boolean {
    const { cursor } = this.doc;
    return this.previewHintShown && cursorIsActive(cursor) && !cursor.cursorHidden;
  }

  /** The preview the caret draws itself, or null while the trailing hint owns it. */
  get cursorPreviewNode(): NodeJson | null {
    return this.previewAtCursor ? this.phantomHintResult : null;
  }

  get hasHints(): boolean {
    return this._hintPositions.length > 0;
  }

  get phantomHints(): PhantomHint[] {
    return this.cursorHintPosition?.variants ?? [];
  }

  get allHintsResolved(): boolean {
    return this.cursorHintPosition?.results.every((r) => r !== undefined) ?? true;
  }

  /** Number of cyclable variants for the cursor hint (0 for table rows, which offer no choice). */
  get phantomHintSize(): number {
    const position = this.cursorHintPosition;
    return position ? position.results.filter((r) => r !== null).length : 0;
  }

  get phantomHintIndex(): number {
    return this._variantIndex;
  }

  /** True when accept-all table-row hints are showing (numeric positions with resolved results). */
  get hasTableRowHints(): boolean {
    return !this.cursorHintPosition && this.tableRowHints.length > 0;
  }

  /** The resolved row hints: their cell content position and computed node. */
  get tableRowHints(): { contentPos: number; node: NodeJson }[] {
    if (this.cursorHintPosition) return [];
    const cells: { contentPos: number; node: NodeJson }[] = [];
    for (const position of this._hintPositions) {
      const node = position.results[this._variantIndex];
      if (node && position.placement !== "head") {
        cells.push({ contentPos: position.placement, node });
      }
    }
    return cells;
  }

  /** Fold the store into the plugin's render items, resolving `"head"` to the live cursor. */
  private get phantomHintItems(): PhantomHintItem[] {
    const head = this.editor.tiptap?.state.selection.head ?? -1;
    const items: PhantomHintItem[] = [];
    for (const position of this._hintPositions) {
      const node = position.results[this._variantIndex];
      if (!node) continue;
      if (position.placement === "head") {
        if (this.previewAtCursor) continue;
        items.push({ pos: head, node });
      } else {
        items.push({ pos: position.placement, node });
      }
    }
    return items;
  }

  private applyPhantomHint() {
    const items = this.phantomHintItems;
    // Dependency highlighting follows the selected variant of the cursor hint only.
    const hint = this.cursorHintPosition?.results[this._variantIndex]
      ? this.cursorHintPosition.variants[this._variantIndex]
      : undefined;
    this.editor.highlightedWrapperIds = new Set(hint?.usedWrapperIds ?? []);
    this.editor.tiptap?.commands.setPhantomHints?.(items.length ? items : null);
  }

  /**
   * Phantom hint is ephemeral and should be reset after any action that modifies the document.
   * It's easier to preserve the hint when needed than conditionally resetting it everywhere.
   */
  preservePhantomHint<T>(fn: () => T) {
    const positions = this._hintPositions;
    const variantIndex = this._variantIndex;

    fn();

    this._hintPositions = positions;
    this._variantIndex = variantIndex;

    this.applyPhantomHint();
  }

  /** Replace the hints with a single cursor-placed hint; `[]` clears them. */
  setPhantomHints(value: PhantomHint[]) {
    return this.resolveHintPositions(value.length ? [makeHintPosition("head", value)] : []);
  }

  /**
   * Show one hint per empty data cell of a formula row, all at once. Cells whose formula yields no
   * real result simply drop out.
   */
  setTableRowHints(cells: TableRowHintCell[]) {
    const positions = cells.flatMap<HintPosition>((cell) => {
      const hint = getTableCellHint(cell);
      return hint ? [makeHintPosition(cell.contentPos, [hint])] : [];
    });
    return this.resolveHintPositions(positions);
  }

  /**
   * True when `next` matches the current positions placement-for-placement and command-for-command,
   * so re-resolving would be wasted. A `"head"` placement never matches — cursor hints always
   * re-resolve.
   */
  private samePositions(next: HintPosition[]): boolean {
    if (this._hintPositions.length !== next.length) return false;
    return this._hintPositions.every((position, i) => {
      const other = next[i];
      if (position.placement === "head") return false;
      return (
        position.placement === other.placement &&
        position.variants.length === other.variants.length &&
        position.variants.every((variant, j) => {
          if (variant.type !== "sympy" || other.variants[j].type !== "sympy") return false;
          return (
            variant.command === other.variants[j].command &&
            variant.expressionText === other.variants[j].expressionText
          );
        })
      );
    });
  }

  /**
   * Resolve hints for one or more positions. Variants are resolved column by column (shared index):
   * the first variant index at which any position yields a real result becomes the selected one.
   * For a cursor hint this is "the first applicable command"; for table rows every cell resolves at
   * index 0 and empties drop out.
   */
  private async resolveHintPositions(next: HintPosition[]) {
    if (this.samePositions(next)) {
      return;
    }

    this.hintCancelTokens.forEach((token) => {
      token.cancelled = true;
    });
    this.hintCancelTokens = [];
    this.shownPhantomHintLabels.clear();

    this._hintPositions = next;
    this._variantIndex = 0;

    if (next.length === 0) {
      this.scheduleHints.cancel();
      this.applyPhantomHint();
      this.hintStatus = "idle";
      return;
    }

    const cancelToken = { cancelled: false };
    this.hintCancelTokens.push(cancelToken);
    this.hintStatus = "computing";

    // Write results through the stored observable positions, not the local `next`: assigning to a
    // deep-observable field clones it, so mutations must target `this._hintPositions`.
    const positions = this._hintPositions;
    const maxVariants = Math.max(...positions.map((position) => position.variants.length));
    let chosen = 0;

    for (let v = 0; v < maxVariants; v += 1) {
      let anyResult = false;
      for (const position of positions) {
        if (v >= position.variants.length) continue;
        const variant = position.variants[v];
        // eslint-disable-next-line no-await-in-loop -- resolve variant v across positions in order
        const resolved = await variant.getHint();
        if (cancelToken.cancelled) return;
        const result = resolved && !resolved.equals ? resolved.json : null;
        this.updateResults(position, v, result);
        if (result) {
          anyResult = true;
          this.trackHintShown(variant);
        }
      }
      if (anyResult) {
        chosen = v;
        break;
      }
    }

    runInAction(() => {
      this.hintStatus = "idle";
      this._variantIndex = chosen;
      this.applyPhantomHint();
    });
  }

  private updateResults(position: HintPosition, variantIndex: number, result: NodeJson | null) {
    position.results[variantIndex] = result;
  }

  private trackHintShown(hint: PhantomHint) {
    // Counting previews would drown the show/accept ratio in hovers.
    if (hint.type === "reference") return;
    if (!this.doc.id || this.shownPhantomHintLabels.has(hint.label)) return;
    this.shownPhantomHintLabels.add(hint.label);
    trackEvent("hints_suggestion_show", {
      hint_type: hint.label,
      expression: hint.expressionText,
      doc_id: this.doc.id,
    });
  }

  /** Cycle the shared variant index over the cursor hint's variants (no-op for table rows). */
  async findNextPhantomHintIndex(direction: 1 | -1 = 1) {
    const position = this.cursorHintPosition;
    if (!position || this.phantomHintSize === 0) return;

    const cancelToken = { cancelled: false };
    this.hintCancelTokens.push(cancelToken);

    const len = position.variants.length;
    let nextIndex = this._variantIndex;

    for (let i = 0; i < len; i += 1) {
      nextIndex = (nextIndex + direction + len) % len;
      if (position.results[nextIndex]) break;
      if (nextIndex === this._variantIndex) break;
      if (position.results[nextIndex] === undefined) {
        // eslint-disable-next-line no-await-in-loop
        const resolved = await position.variants[nextIndex].getHint();
        if (cancelToken.cancelled) return;

        if (resolved?.equals || !resolved?.json) {
          position.results[nextIndex] = null;
        } else {
          const duplicate = position.results.some((json) => json && isEqual(json, resolved.json));
          position.results[nextIndex] = duplicate ? null : resolved.json;
        }

        if (position.results[nextIndex]) break;
      }
    }

    runInAction(() => {
      this._variantIndex = nextIndex;
    });
  }
}
